(() => {
  if (window.Veloire9X) return;

  const state = {
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)'),
    compactViewport: window.matchMedia('(max-width: 749px)'),
    saveData: Boolean(navigator.connection && navigator.connection.saveData)
  };

  const setupStickyOffsets = () => {
    const root = document.documentElement;
    const siteHeader = document.querySelector('.header-section');
    const chapterNav = document.querySelector('.v9x-nav');
    const chapterNavShell = chapterNav?.parentElement;
    const observed = [siteHeader, chapterNav].filter(Boolean);

    if (chapterNavShell) chapterNavShell.classList.add('v9x-nav-shell');

    const update = () => {
      const headerStyle = siteHeader ? window.getComputedStyle(siteHeader) : null;
      const headerIsSticky = headerStyle && ['sticky', 'fixed'].includes(headerStyle.position);
      const headerHeight = headerIsSticky ? Math.max(0, Math.round(siteHeader.getBoundingClientRect().height)) : 0;
      const navHeight = chapterNav ? Math.max(0, Math.round(chapterNav.getBoundingClientRect().height)) : 0;

      root.style.setProperty('--v9x-site-header-height', `${headerHeight}px`);
      root.style.setProperty('--v9x-subnav-height', `${navHeight}px`);
    };

    update();
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(update);
      observed.forEach((element) => observer.observe(element));
    }
    window.addEventListener('resize', update, { passive: true });
  };

  const getSources = (video) => {
    const sources = [...video.querySelectorAll('source[data-src]')];
    const matching = sources.filter((source) => {
      const query = source.dataset.media;
      return !query || window.matchMedia(query).matches;
    });

    const playable = matching.filter((source) => {
      const type = source.dataset.type;
      return !type || video.canPlayType(type) !== '';
    });

    const adaptiveCandidates = playable.filter((source) => /mpegurl/i.test(source.dataset.type || ''));
    const adaptive = video.loop ? [] : adaptiveCandidates;
    const progressiveCandidates = playable.filter((source) => !adaptiveCandidates.includes(source));

    const targetWidth = Math.min(
      2400,
      Math.ceil((video.getBoundingClientRect().width || window.innerWidth) * Math.min(window.devicePixelRatio || 1, 2))
    );
    const measured = progressiveCandidates
      .filter((source) => Number(source.dataset.width) > 0)
      .sort((a, b) => Number(a.dataset.width) - Number(b.dataset.width));
    const preferred = measured.find((source) => Number(source.dataset.width) >= targetWidth) || measured[measured.length - 1];
    const progressive = [
      preferred,
      ...measured
        .filter((source) => source !== preferred)
        .sort((a, b) => (
          Math.abs(Number(a.dataset.width) - targetWidth) - Math.abs(Number(b.dataset.width) - targetWidth)
        )),
      ...progressiveCandidates.filter((source) => !measured.includes(source))
    ].filter(Boolean);

    const ordered = [...new Set([...adaptive, ...progressive])];
    return ordered.length ? ordered : matching;
  };

  const updateControl = (media) => {
    const video = media.querySelector('[data-v9x-video]');
    const control = media.querySelector('[data-v9x-video-toggle]');
    if (!video || !control) return;

    const playing = !video.paused && !video.ended;
    const label = playing ? 'Pause film' : 'Play film';
    control.setAttribute('aria-label', label);
    control.setAttribute('aria-pressed', playing ? 'true' : 'false');
    const accessibleLabel = control.querySelector('[data-v9x-video-label]');
    if (accessibleLabel) accessibleLabel.textContent = label;
  };

  const hydrate = (media, shouldPlay = false) => {
    const video = media.querySelector('[data-v9x-video]');
    if (!video) return Promise.resolve();

    if (!video.dataset.hydrated) {
      const activeSources = getSources(video);
      if (!activeSources.length) return Promise.resolve();

      const allSources = [...video.querySelectorAll('source[data-src]')];
      const inactiveSources = allSources.filter((source) => !activeSources.includes(source));
      activeSources.forEach((source) => {
        source.src = source.dataset.src;
      });
      [...activeSources, ...inactiveSources].forEach((source) => video.appendChild(source));
      video.dataset.hydrated = 'true';
      video.load();
    }

    if (!shouldPlay) return Promise.resolve();
    return video.play().catch(() => undefined);
  };

  const setupMedia = (media) => {
    if (media.dataset.v9xMediaReady === 'true') return;
    const video = media.querySelector('[data-v9x-video]');
    const control = media.querySelector('[data-v9x-video-toggle]');
    if (!video) return;
    media.dataset.v9xMediaReady = 'true';

    if (control) {
      control.hidden = getSources(video).length === 0;
      control.addEventListener('click', async () => {
        if (video.paused) {
          await hydrate(media, true);
        } else {
          video.pause();
        }
        updateControl(media);
      });
    }

    video.addEventListener('playing', () => {
      media.classList.add('is-playing');
      updateControl(media);
    });
    video.addEventListener('pause', () => {
      media.classList.remove('is-playing');
      updateControl(media);
    });
    video.addEventListener('ended', () => {
      media.classList.remove('is-playing');
      updateControl(media);
    });

    const wantsAutoplay = video.dataset.autoplay === 'true';
    if (wantsAutoplay && !state.reducedMotion.matches && !state.saveData) {
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(async (entry) => {
            if (entry.isIntersecting) {
              await hydrate(media, true);
              observer.disconnect();
            }
          });
        }, { rootMargin: '240px 0px' });
        observer.observe(media);
      } else {
        hydrate(media, true);
      }
    }

    updateControl(media);
  };

  const refreshResponsiveMedia = () => {
    document.querySelectorAll('[data-v9x-media]').forEach((media) => {
      const video = media.querySelector('[data-v9x-video]');
      if (!video || !video.dataset.hydrated) return;

      const wasPlaying = !video.paused && !video.ended;
      video.pause();
      video.querySelectorAll('source[data-src]').forEach((source) => source.removeAttribute('src'));
      delete video.dataset.hydrated;
      video.removeAttribute('src');
      video.load();
      hydrate(media, wasPlaying).then(() => updateControl(media));
    });
  };

  const populateAttribution = (form) => {
    if (form.dataset.v9xAttributionReady === 'true') return;
    form.dataset.v9xAttributionReady = 'true';
    const params = new URLSearchParams(window.location.search);
    const mappings = {
      utm_source: 'utm-source',
      utm_medium: 'utm-medium',
      utm_campaign: 'utm-campaign',
      utm_content: 'utm-content'
    };

    Object.entries(mappings).forEach(([parameter, field]) => {
      const input = form.querySelector(`[data-v9x-${field}]`);
      if (input) input.value = params.get(parameter) || '';
    });

    const referrer = form.querySelector('[data-v9x-referrer]');
    if (referrer) referrer.value = document.referrer || '';

    form.addEventListener('submit', () => {
      const submittedAt = form.querySelector('[data-v9x-submitted-at]');
      if (submittedAt) submittedAt.value = new Date().toISOString();
    });
  };

  const setupAdvisorTracking = (advisor) => {
    if (advisor.dataset.v9xAdvisorReady === 'true') return;
    advisor.dataset.v9xAdvisorReady = 'true';

    advisor.addEventListener('click', (event) => {
      const link = event.target.closest('[data-event="specialist_contact_click"]');
      if (!link || !advisor.contains(link)) return;

      const eventName = link.dataset.event;
      const method = link.dataset.contactMethod || 'unknown';
      const context = link.dataset.contactContext || 'veloire-advisor';
      const payload = {
        event: eventName,
        specialist_contact_method: method,
        specialist_contact_context: context,
        specialist_contact_label: link.textContent.trim(),
        page_location: window.location.href,
        page_path: window.location.pathname
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);

      try {
        if (typeof window.gtag === 'function' && !window.google_tag_manager) {
          window.gtag('event', eventName, {
            contact_method: method,
            contact_context: context,
            event_category: 'specialist_contact',
            event_label: method
          });
        }
      } catch (error) {
        // Contact navigation should never be blocked by analytics.
      }

      try {
        if (window.Shopify?.analytics?.publish) {
          window.Shopify.analytics.publish(eventName, {
            contactMethod: method,
            contactContext: context,
            pagePath: window.location.pathname
          });
        }
      } catch (error) {
        // Contact navigation should never be blocked by analytics.
      }
    });
  };

  const setupSpecification = (specification) => {
    if (specification.dataset.v9xSpecificationReady === 'true') return;
    specification.dataset.v9xSpecificationReady = 'true';

    const tabs = [...specification.querySelectorAll('[data-v9x-specification-tab]')];
    const panels = [...specification.querySelectorAll('[data-v9x-specification-panel]')];
    if (!tabs.length || !panels.length) return;

    const activate = (tab, moveFocus = false) => {
      const target = tab.dataset.v9xSpecificationTab;

      tabs.forEach((candidate) => {
        const selected = candidate === tab;
        candidate.classList.toggle('is-active', selected);
        candidate.setAttribute('aria-selected', selected ? 'true' : 'false');
        candidate.tabIndex = selected ? 0 : -1;
      });

      panels.forEach((panel) => {
        const selected = panel.dataset.v9xSpecificationPanel === target;
        panel.hidden = !selected;
        panel.classList.toggle('is-active', selected);
      });

      if (moveFocus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        activate(tabs[nextIndex], true);
      });
    });

    specification.addEventListener('shopify:block:select', (event) => {
      const panel = event.target.closest('[data-v9x-specification-panel]');
      if (!panel) return;
      const tab = tabs.find((candidate) => (
        candidate.dataset.v9xSpecificationTab === panel.dataset.v9xSpecificationPanel
      ));
      if (tab) activate(tab);
    });

    const catalog = specification.querySelector('[data-v9x-catalog-download]');
    if (catalog) {
      catalog.addEventListener('click', () => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'catalog_download',
          catalog_name: 'veloire_9x_specification',
          page_path: window.location.pathname
        });
      });
    }
  };

  // ===== VELOIRE specification: body kit, carbon finish and forged wheels =====
  const v9xIsZh = Boolean(document.querySelector('[data-v9x-language="zh-CN"]'));
  const v9xFinishLabel = (name) => v9xIsZh ? ({
    'Gloss twill': '亮光斜纹',
    'Matte twill': '哑光斜纹',
    'Forged carbon fibre': '锻造碳纤维',
    'Other commissioned finish': '定制饰面'
  }[name] || name) : name;
  const specificationState = {
    components: new Map(),
    finish: { name: 'Gloss twill', multiplier: 1, onRequest: false },
    wheel: null
  };

  const finishOptions = {
    'Gloss twill': { name: 'Gloss twill', multiplier: 1, onRequest: false },
    'Matte twill': { name: 'Matte twill', multiplier: 1.1, onRequest: false },
    'Forged carbon fibre': { name: 'Forged carbon fibre', multiplier: 1.1, onRequest: false },
    'Other commissioned finish': { name: 'Other commissioned finish', multiplier: null, onRequest: true }
  };

  const wheelOptions = {
    '23-inch': { size: '23-inch', price: 4700, sku: 'W121-24150' },
    '24-inch': { size: '24-inch', price: 5200, sku: 'W121-24150' }
  };

  const money = (amount) => {
    const value = Math.round((Number(amount) || 0) * 100) / 100;
    const decimals = Number.isInteger(value) ? 0 : 2;
    return `USD $${value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })}`;
  };

  const setText = (selector, value) => {
    document.querySelectorAll(selector).forEach((element) => {
      element.textContent = value;
    });
  };

  const getComponentCatalog = () => {
    const catalog = new Map();
    document.querySelectorAll('[data-v9x-select-sku]').forEach((button) => {
      const sku = button.dataset.v9xSelectSku;
      if (!sku || catalog.has(sku)) return;
      catalog.set(sku, {
        sku,
        name: button.dataset.v9xSelectName || sku,
        price: Number(button.dataset.v9xSelectPrice) || 0
      });
    });
    return catalog;
  };

  const getTotals = () => {
    const components = Array.from(specificationState.components.values());
    const componentSubtotal = components.reduce((sum, component) => sum + component.price, 0);
    const finishSurcharge = specificationState.finish.onRequest && componentSubtotal > 0
      ? null
      : specificationState.finish.onRequest
        ? 0
        : componentSubtotal * (specificationState.finish.multiplier - 1);
    const wheelPrice = specificationState.wheel?.price || 0;
    const total = finishSurcharge === null ? null : componentSubtotal + finishSurcharge + wheelPrice;
    return { componentSubtotal, finishSurcharge, wheelPrice, total };
  };

  const toggleComponent = (button) => {
    const component = {
      sku: button.dataset.v9xSelectSku,
      name: button.dataset.v9xSelectName || button.dataset.v9xSelectSku,
      price: Number(button.dataset.v9xSelectPrice) || 0
    };
    if (specificationState.components.has(component.sku)) {
      specificationState.components.delete(component.sku);
    } else {
      specificationState.components.set(component.sku, component);
    }
    refreshSpecification();
  };

  const selectAllComponents = () => {
    const catalog = getComponentCatalog();
    const allSelected = catalog.size > 0 && specificationState.components.size === catalog.size;
    specificationState.components.clear();
    if (!allSelected) {
      catalog.forEach((component, sku) => specificationState.components.set(sku, component));
    }
    refreshSpecification();
  };

  const resetSpecification = () => {
    specificationState.components.clear();
    specificationState.finish = { ...finishOptions['Gloss twill'] };
    specificationState.wheel = null;
    refreshSpecification();
  };

  const syncComponentControls = (catalog) => {
    document.querySelectorAll('[data-v9x-select-sku]').forEach((button) => {
      const selected = specificationState.components.has(button.dataset.v9xSelectSku);
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.setAttribute('aria-label', `${selected ? (v9xIsZh ? '取消选择' : 'Deselect') : (v9xIsZh ? '选择' : 'Select')} ${button.dataset.v9xSelectName}`);
      button.closest('[data-v9x-atlas-card]')?.classList.toggle('is-selected', selected);
      const label = button.querySelector('.v9x-atlas__select-label');
      if (label) label.textContent = selected ? (v9xIsZh ? '已选择 ✓' : 'Selected ✓') : (v9xIsZh ? '选择' : 'Select');
    });

    const allSelected = catalog.size > 0 && specificationState.components.size === catalog.size;
    document.querySelectorAll('[data-v9x-select-all]').forEach((button) => {
      button.classList.toggle('is-selected', allSelected);
      button.setAttribute('aria-pressed', allSelected ? 'true' : 'false');
      const label = button.querySelector('[data-v9x-select-all-state]');
      if (label) label.textContent = allSelected ? (v9xIsZh ? '已选择完整套件 ✓' : 'COMPLETE KIT SELECTED ✓') : (v9xIsZh ? '选择完整套件' : 'SELECT COMPLETE KIT');
    });
  };

  const syncFinishControls = () => {
    document.querySelectorAll('[data-v9x-finish-option]').forEach((input) => {
      input.checked = input.dataset.v9xFinishName === specificationState.finish.name;
    });
  };

  const syncWheelControls = () => {
    document.querySelectorAll('[data-v9x-wheel-option]').forEach((button) => {
      const selected = button.dataset.v9xWheelSize === specificationState.wheel?.size;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.setAttribute('aria-label', `${selected ? (v9xIsZh ? '移除' : 'Remove') : (v9xIsZh ? '选择' : 'Select')} ${button.dataset.v9xWheelSize} ${v9xIsZh ? 'VELOIRE 锻造轮毂' : 'VELOIRE forged wheels'}`);
    });
    document.querySelectorAll('[data-v9x-wheel-card]').forEach((card) => {
      card.classList.toggle('is-selected', Boolean(specificationState.wheel));
    });
    setText(
      '[data-v9x-wheel-status]',
      specificationState.wheel
        ? `${specificationState.wheel.size} ${v9xIsZh ? '已选择' : 'selected'} · ${money(specificationState.wheel.price)}`
        : (v9xIsZh ? '选择23英寸或24英寸。' : 'Select 23-inch or 24-inch.')
    );
  };

  const syncSummary = (catalog, totals) => {
    const componentCount = specificationState.components.size;
    const allSelected = catalog.size > 0 && componentCount === catalog.size;
    const displayFinishName = v9xFinishLabel(specificationState.finish.name);
    const finishLabel = specificationState.finish.onRequest
      ? `${displayFinishName} · ${v9xIsZh ? '按需报价' : 'pricing on request'}`
      : specificationState.finish.multiplier === 1
        ? `${displayFinishName} · ${v9xIsZh ? '已包含' : 'included'}`
        : `${displayFinishName} · +10%`;
    const wheelLabel = specificationState.wheel
      ? `${specificationState.wheel.size} · ${money(specificationState.wheel.price)}`
      : (v9xIsZh ? '未选择' : 'Not selected');
    const finishAdjustment = totals.finishSurcharge === null
      ? (v9xIsZh ? '价格按需确认' : 'Pricing confirmed on request')
      : specificationState.finish.onRequest
        ? (v9xIsZh ? '所选碳纤维组件按需报价' : 'Pricing on request for selected carbon components')
      : specificationState.finish.multiplier === 1
        ? (v9xIsZh ? '已包含' : 'Included')
        : totals.componentSubtotal > 0
          ? `+10% · ${money(totals.finishSurcharge)}`
          : (v9xIsZh ? '所选碳纤维组件加价10%' : '+10% on selected carbon components');
    const totalLabel = totals.total === null ? (v9xIsZh ? '价格按需确认' : 'Pricing confirmed on request') : money(totals.total);

    document.querySelectorAll('[data-v9x-sel-summary]').forEach((summary) => {
      summary.hidden = false;
    });
    setText('[data-v9x-sel-count]', String(componentCount));
    setText('[data-v9x-sel-mode]', allSelected ? (v9xIsZh ? '完整宽体套件' : 'Complete body kit') : (v9xIsZh ? '自定义配置' : 'Custom specification'));
    setText('[data-v9x-finish-summary]', finishLabel);
    setText('[data-v9x-wheel-summary]', wheelLabel);
    setText('[data-v9x-component-subtotal]', money(totals.componentSubtotal));
    setText('[data-v9x-finish-surcharge]', finishAdjustment);
    setText('[data-v9x-estimated-total]', totalLabel);
    setText('[data-v9x-enq-finish]', finishLabel);
    setText('[data-v9x-enq-wheel]', wheelLabel);
    setText('[data-v9x-enq-total]', totalLabel);
  };

  const syncBuildComponentList = () => {
    document.querySelectorAll('[data-v9x-build-components]').forEach((display) => {
      const empty = display.querySelector('[data-v9x-build-empty]');
      if (empty) empty.hidden = specificationState.components.size > 0;
      let list = display.querySelector('.v9x-build-summary__component-list');

      if (specificationState.components.size === 0) {
        if (list) list.remove();
        return;
      }

      if (!list) {
        list = document.createElement('ul');
        list.className = 'v9x-build-summary__component-list';
        display.appendChild(list);
      }
      list.innerHTML = '';
      specificationState.components.forEach((component) => {
        const item = document.createElement('li');
        item.textContent = `${component.name} · ${component.sku}`;
        list.appendChild(item);
      });
    });
  };

  const syncForm = (totals) => {
    const componentsValue = Array.from(specificationState.components.values())
      .map((component) => `${component.sku} — ${component.name} — ${money(component.price)}`)
      .join('; ');
    const finishValue = specificationState.finish.onRequest
      ? `${specificationState.finish.name} — pricing confirmed on request`
      : specificationState.finish.multiplier === 1
        ? `${specificationState.finish.name} — included`
        : `${specificationState.finish.name} — +10% on selected carbon components`;
    const finishAdjustment = totals.finishSurcharge === null
      ? 'Pricing confirmed on request'
      : specificationState.finish.onRequest
        ? 'Pricing on request for selected carbon components'
      : specificationState.finish.multiplier === 1
        ? 'Included'
        : money(totals.finishSurcharge);
    const wheelValue = specificationState.wheel
      ? `${specificationState.wheel.sku} — ${specificationState.wheel.size} — ${money(specificationState.wheel.price)}`
      : 'Not selected';
    const totalValue = totals.total === null ? 'Pricing confirmed on request' : money(totals.total);

    const formPayload = [
      ['[data-v9x-selected-skus]', componentsValue],
      ['[data-v9x-selected-finish]', finishValue],
      ['[data-v9x-finish-adjustment]', finishAdjustment],
      ['[data-v9x-components-subtotal]', money(totals.componentSubtotal)],
      ['[data-v9x-selected-wheel]', wheelValue],
      ['[data-v9x-wheel-price]', money(totals.wheelPrice)],
      ['[data-v9x-specification-total]', totalValue]
    ];
    document.querySelectorAll('[data-v9x-enquiry-form] form').forEach((form) => {
      let syncedFields = 0;
      formPayload.forEach(([selector, value]) => {
        const input = form.querySelector(selector);
        if (!input) return;
        input.setAttribute('value', value);
        input.defaultValue = value;
        input.value = value;
        syncedFields += 1;
      });
      form.dataset.v9xSpecificationSynced = syncedFields === formPayload.length ? 'true' : 'partial';
      form.dataset.v9xSpecificationFields = String(syncedFields);
    });

    document.querySelectorAll('[data-v9x-form-finish]').forEach((select) => {
      if (select.value !== specificationState.finish.name) select.value = specificationState.finish.name;
    });
    document.querySelectorAll('[data-v9x-form-wheel]').forEach((select) => {
      const value = specificationState.wheel?.size || '';
      if (select.value !== value) select.value = value;
    });
  };

  const syncEnquiryComponentList = () => {
    document.querySelectorAll('[data-v9x-sel-display]').forEach((display) => {
      const empty = display.querySelector('[data-v9x-sel-empty]');
      if (empty) empty.hidden = specificationState.components.size > 0;
      let list = display.querySelector('.v9x-enq2__selected-list');

      if (specificationState.components.size === 0) {
        if (list) list.remove();
        return;
      }

      if (!list) {
        list = document.createElement('ul');
        list.className = 'v9x-enq2__selected-list';
        display.prepend(list);
      }
      list.innerHTML = '';
      specificationState.components.forEach((component) => {
        const item = document.createElement('li');
        const name = document.createElement('span');
        const meta = document.createElement('small');
        name.textContent = component.name;
        meta.textContent = `${component.sku} · ${money(component.price)}`;
        item.append(name, meta);
        list.appendChild(item);
      });
    });
  };

  const refreshSpecification = () => {
    const catalog = getComponentCatalog();
    const totals = getTotals();
    syncComponentControls(catalog);
    syncFinishControls();
    syncWheelControls();
    syncSummary(catalog, totals);
    syncBuildComponentList();
    syncEnquiryComponentList();
    syncForm(totals);
  };

  const setupSelection = (root) => {
    root.querySelectorAll('[data-v9x-select-sku]').forEach((button) => {
      if (button.dataset.v9xSelectReady) return;
      button.dataset.v9xSelectReady = 'true';
      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', () => toggleComponent(button));
    });

    root.querySelectorAll('[data-v9x-select-all]').forEach((button) => {
      if (button.dataset.v9xSelectReady) return;
      button.dataset.v9xSelectReady = 'true';
      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', selectAllComponents);
    });

    root.querySelectorAll('[data-v9x-sel-clear]').forEach((button) => {
      if (button.dataset.v9xSelectReady) return;
      button.dataset.v9xSelectReady = 'true';
      button.addEventListener('click', resetSpecification);
    });

    root.querySelectorAll('[data-v9x-finish-option]').forEach((input) => {
      if (input.dataset.v9xSelectReady) return;
      input.dataset.v9xSelectReady = 'true';
      input.addEventListener('change', () => {
        if (!input.checked) return;
        specificationState.finish = {
          name: input.dataset.v9xFinishName,
          multiplier: Number(input.dataset.v9xFinishMultiplier),
          onRequest: false
        };
        refreshSpecification();
      });
    });

    root.querySelectorAll('[data-v9x-wheel-option]').forEach((button) => {
      if (button.dataset.v9xSelectReady) return;
      button.dataset.v9xSelectReady = 'true';
      button.addEventListener('click', () => {
        const size = button.dataset.v9xWheelSize;
        specificationState.wheel = specificationState.wheel?.size === size
          ? null
          : {
              size,
              price: Number(button.dataset.v9xWheelPrice) || 0,
              sku: button.dataset.v9xWheelSku || 'W121-24150'
            };
        refreshSpecification();
      });
    });

    root.querySelectorAll('[data-v9x-form-finish]').forEach((select) => {
      if (select.dataset.v9xSelectReady) return;
      select.dataset.v9xSelectReady = 'true';
      select.addEventListener('change', () => {
        specificationState.finish = { ...(finishOptions[select.value] || finishOptions['Gloss twill']) };
        refreshSpecification();
      });
    });

    root.querySelectorAll('[data-v9x-form-wheel]').forEach((select) => {
      if (select.dataset.v9xSelectReady) return;
      select.dataset.v9xSelectReady = 'true';
      select.addEventListener('change', () => {
        specificationState.wheel = select.value ? { ...wheelOptions[select.value] } : null;
        refreshSpecification();
      });
    });

    root.querySelectorAll('[data-v9x-enquiry-form] form').forEach((form) => {
      if (form.dataset.v9xSelSyncReady) return;
      form.dataset.v9xSelSyncReady = 'true';
      form.addEventListener('submit', refreshSpecification, { capture: true });
    });

    refreshSpecification();
  };

  const setupAtlas = (atlas) => {
    if (atlas.dataset.v9xAtlasReady === 'true') return;
    atlas.dataset.v9xAtlasReady = 'true';

    const media = atlas.querySelector('[data-v9x-atlas-media]');
    const cards = [...atlas.querySelectorAll('[data-v9x-atlas-card]')];
    if (!media || !cards.length) return;

    const mediaItems = [...media.querySelectorAll('[data-v9x-atlas-img]')];
    if (!mediaItems.length) return;

    const activate = (card) => {
      const num = card.dataset.v9xAtlasCard;
      const item = mediaItems.find((mi) => mi.dataset.v9xAtlasImg === num);
      if (item) {
        mediaItems.forEach((mi) => mi.classList.toggle('is-active', mi === item));
      }
    };

    cards.forEach((card) => {
      card.addEventListener('mouseenter', () => activate(card));
      card.addEventListener('focusin', () => activate(card));
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) activate(entry.target);
        });
      }, { rootMargin: '-40% 0px -40% 0px' });
      cards.forEach((card) => observer.observe(card));
    }
  };

  const init = (root = document) => {
    setupStickyOffsets();
    root.querySelectorAll('[data-v9x-media]').forEach(setupMedia);
    root.querySelectorAll('[data-v9x-enquiry-form] form').forEach(populateAttribution);
    root.querySelectorAll('[data-v9x-advisor]').forEach(setupAdvisorTracking);
    root.querySelectorAll('[data-v9x-specification]').forEach(setupSpecification);
    root.querySelectorAll('[data-v9x-atlas]').forEach(setupAtlas);
    setupSelection(root);

    const status = root.querySelector('[data-v9x-form-status]');
    if (status) window.requestAnimationFrame(() => status.focus());
  };

  document.addEventListener('DOMContentLoaded', () => {
    init();
    state.compactViewport.addEventListener('change', refreshResponsiveMedia);
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.Veloire9X = { init, hydrate };
})();
